import {
    configureStore,
    Action as ReduxAction,
    Dispatch as ReduxDispatch,
    Store,
    AnyAction as AnyReduxAction,
} from '@reduxjs/toolkit';
import { ThunkAction, ThunkDispatch as ReduxThunkDispatch } from 'redux-thunk';
import { reduxBatch } from '@manaflair/redux-batch';
import rootReducer from './rootReducer';
import Services from './Services';
import State, { initialState } from './State';
import { notificationsMiddleware } from 'notification/store/middleware';
import { portfolioMiddleware } from 'portfolio/store/middleware';
import * as loggingWriterMiddleware from 'logging/store/middleware';
import * as appStore from 'app/store/appStore';

import { errorHandlerMiddleware } from './errorHandlerMiddleware';
import { layoutMiddleware } from 'layout/store/middleware';
import { updateOrdersStreamOnSymbologyChangedMiddleware } from 'app/store/appSettings/middleware';
import { positionEntryMiddleware } from 'positionEntry/store/middleware';
import { blotterMiddleware } from 'widgets/blotter/store/middleware';
import { portfolioSummaryMiddleware } from 'widgets/portfolioSummary/store/data/middleware';
import { ordersStreamMiddleware } from 'app/store/orders/middleware';
import { updateWidgetsSharedStateMiddleware } from 'widgets/store/shared/middleware';
import { clickstreamMiddleware } from 'clickstream/store/middleware';
import { embeddedAppMiddleware } from 'embedded/store/middleware';
import { widgetStreamingMiddleware } from 'widgets/streaming/middleware';
import { embeddedApiMiddleware } from 'embedded/api/store/middleware';
import { myPortfolioCapsulesMiddleware } from 'widgets/myPortfolioCapsules/store/middleware';
import { capsuleContainerMiddleware } from 'widgets/tiles/store/middleware';
import { widgetConfigMiddleware } from 'widgets/store/configuration/middleware';
import { registerAppEffects } from 'store/effects';
import { createListenerMiddleware } from 'store/listenerMiddleware';
import { NotificationAction } from 'notification/store/actions';

interface StoreOptions {
    services: Services;
}

export default function createStore(options: StoreOptions): IndigoStore {
    const { services } = options;

    const listenerMiddleware = createListenerMiddleware(services);

    const store = configureStore({
        reducer: rootReducer,
        devTools: false,
        preloadedState: initialState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: { extraArgument: options.services },
                immutableCheck: false, // does not play nicely with redux-batch (ie cannot ignore orders etc)
                serializableCheck: false, // does not play nicely with redux-batch
            })
                .prepend(
                    errorHandlerMiddleware(services),
                    ...portfolioMiddleware(services),
                    ...widgetStreamingMiddleware(services),
                    ...blotterMiddleware(services),
                    ...embeddedAppMiddleware(services),
                    ...embeddedApiMiddleware(services),
                    updateOrdersStreamOnSymbologyChangedMiddleware(services),
                    ...layoutMiddleware(services),
                    updateWidgetsSharedStateMiddleware(services),
                    ...widgetConfigMiddleware(services),
                    ...ordersStreamMiddleware(services),
                    ...positionEntryMiddleware(services),
                    ...notificationsMiddleware(),
                    ...portfolioSummaryMiddleware(services),
                    ...myPortfolioCapsulesMiddleware(services),
                    ...capsuleContainerMiddleware(services),
                    ...appStore.middleware(services),
                    listenerMiddleware.middleware,
                    ...loggingWriterMiddleware.middleware(services)
                )
                .concat(clickstreamMiddleware(services)),
        enhancers: (defaultEnhancers) => [reduxBatch, ...defaultEnhancers],
    }) as IndigoStore;

    registerAppEffects(listenerMiddleware);

    return store;
}

export type Dispatch<Action extends ReduxAction> = ReduxDispatch<Action>;

export type ThunkDispatch<Action extends ReduxAction> = ReduxThunkDispatch<
    State,
    Services,
    Action
>;

export interface Action<T, P = undefined> extends ReduxAction<T> {
    payload?: P;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyAction = Action<any>;

export type AnyThunkAction<ReturnType = void> = ThunkAction<
    ReturnType,
    State,
    unknown,
    Action<string>
>;

type IndigoThunkAction = (
    dispatch: ThunkDispatch<NotificationAction>,
    getState: () => State,
    services: Services
) => Promise<void>;

type SupportedDisatchPrm =
    | AnyReduxAction
    | AnyReduxAction[]
    | AnyThunkAction
    | IndigoThunkAction;

export type IndigoStore = Omit<Store<State, AnyReduxAction>, 'dispatch'> & {
    dispatch: <T extends SupportedDisatchPrm>(action: T) => T;
};

export type IndigoDispatch = IndigoStore['dispatch'];
